if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // 100ms, not Dawn's 500: half a second between swiping the hero and
        // the thumbnail highlight following reads as broken sync. 100 still
        // coalesces scroll-event floods but feels simultaneous.
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));

        // The thumbnail outline follows the hero ONLY for user-driven
        // movement (swipe, wheel, keys). Programmatic jumps announce
        // themselves through setActiveMedia, which sets the thumbnail
        // directly — and the slider emits spurious slideChanged events
        // while layout settles during page load, which used to drag the
        // outline to a slide nobody chose. No interaction, no follow.
        this._userInteracting = false;
        const markInteracting = () => {
          this._userInteracting = true;
          clearTimeout(this._interactionIdle);
          this._interactionIdle = setTimeout(() => { this._userInteracting = false; }, 1200);
        };
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((evt) =>
          this.elements.viewer.addEventListener(evt, markInteracting, { passive: true })
        );
        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.addEventListener('scroll', () => {
            if (this._userInteracting) markInteracting();
            this.scheduleStripFollow();
          }, { passive: true });
        }

        // A drag on the strip itself belongs to the shopper — the hero's
        // momentum must not steal it back mid-gesture.
        ['pointerdown', 'touchstart', 'wheel'].forEach((evt) =>
          this.elements.thumbnails.addEventListener(evt, () => {
            this._stripInteracting = true;
            clearTimeout(this._stripIdle);
            this._stripIdle = setTimeout(() => { this._stripInteracting = false; }, 1200);
          }, { passive: true })
        );
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        // Where the hero scrolls, followHero tracks the outline continuously
        // from the real scroll offset. This debounced page-level signal is
        // both slower and coarser, so letting it through would only fight it.
        if (this.heroDrivesStrip()) return;
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (this._syncTarget && Date.now() < this._syncTargetUntil && mediaId !== this._syncTarget) return;
        if (!this._userInteracting) return;
        this._syncTarget = null;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // Sync pin: this is a PROGRAMMATIC jump to a known slide, so the
        // pages the viewer glides past on the way must not drag the active
        // thumbnail with them (the "shuffle"). onSlideChanged ignores
        // intermediate pages until the scroll lands here or the pin expires.
        // Manual swipes never set a pin and keep live thumb feedback.
        this._syncTarget = mediaId;
        this._syncTargetUntil = Date.now() + 1200;
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // ── Strip ⇄ hero sync ──────────────────────────────────────────────
      // The thumbnail strip mirrors the hero's LIVE scroll position, frame by
      // frame, so the two carousels behave like one object: a swipe drags the
      // strip along with it, and a programmatic glide (arrow, wrap, thumbnail
      // click) carries it on the same easing curve. It replaces a debounced
      // "once the active thumb falls off the edge, jump the strip a page",
      // which read as a delayed, choppy re-page at every third thumb.

      // Track measurements, cached. The key is derived from the numbers that
      // change when the visible set does (variant filtering) or the viewport
      // resizes, so the scroll handler re-measures only when it must.
      galleryGeometry() {
        const hero = this.elements.viewer && this.elements.viewer.slider;
        const strip = this.elements.thumbnails && this.elements.thumbnails.slider;
        if (!hero || !strip) return null;
        const key = hero.scrollWidth + ':' + strip.scrollWidth + ':' + strip.clientWidth;
        if (this._geo && this._geo.key === key) return this._geo;
        const shown = (el) => getComputedStyle(el).display !== 'none';
        const slides = Array.from(hero.querySelectorAll('.product__media-item')).filter(shown);
        const thumbs = Array.from(strip.querySelectorAll('.thumbnail-list__item')).filter(shown);
        // The two lists are filtered by the same alt-text rule, so a mismatch
        // means we are measuring mid-update — bail rather than sync to a map
        // that pairs the wrong slide with the wrong thumb.
        if (slides.length < 2 || slides.length !== thumbs.length) return null;
        this._geo = {
          key,
          strip,
          thumbs,
          slideCentres: slides.map((el) => el.offsetLeft + el.offsetWidth / 2),
          heroHalf: hero.clientWidth / 2,
          thumbCentres: thumbs.map((el) => el.offsetLeft + el.offsetWidth / 2),
          half: strip.clientWidth / 2,
          max: Math.max(0, strip.scrollWidth - strip.clientWidth),
        };
        return this._geo;
      }

      // Where the hero is, as a FRACTIONAL slide index — mid-swipe included.
      // Measured from what the viewport is CENTRED on, not from raw offsets:
      // the hero's snap positions are set by its own scroll-padding and
      // snap-align, so a slide at rest sits at a scrollLeft that is not its
      // offsetLeft. Centre-to-centre is independent of all of that, and lands
      // within a few hundredths of a whole index when a slide is at rest.
      heroFraction(geo, x) {
        const c = geo.slideCentres;
        const viewCentre = x + geo.heroHalf;
        if (viewCentre <= c[0]) return 0;
        for (let i = 0; i < c.length - 1; i++) {
          if (viewCentre < c[i + 1]) return i + (viewCentre - c[i]) / (c[i + 1] - c[i]);
        }
        return c.length - 1;
      }

      // Strip offset that keeps slide `f` centred, clamped to the track.
      // Fractional in, fractional out: the strip glides continuously with the
      // hero rather than stepping when a thumb crosses the edge. Centring also
      // makes every step the same distance, so there is no "three still, then
      // a lurch" — that uniformity is what reads as smooth.
      stripLeftFor(geo, f) {
        const c = geo.thumbCentres;
        const i = Math.max(0, Math.min(c.length - 1, Math.floor(f)));
        const j = Math.min(c.length - 1, i + 1);
        const centre = c[i] + (c[j] - c[i]) * (f - i);
        return Math.max(0, Math.min(geo.max, centre - geo.half));
      }

      // True when the hero is a real scroller (mobile, and any layout where it
      // pages). Then the hero owns the strip's position and nothing else may
      // scroll it, or the two fight and the strip arrives before the image.
      heroDrivesStrip() {
        const hero = this.elements.viewer && this.elements.viewer.slider;
        return !!hero && hero.scrollWidth > hero.clientWidth + 4;
      }

      scheduleStripFollow() {
        if (this._followQueued) return;
        this._followQueued = true;
        requestAnimationFrame(() => {
          this._followQueued = false;
          this.followHero();
        });
      }

      followHero() {
        const geo = this.galleryGeometry();
        if (!geo || this._stripInteracting) return;
        const f = this.heroFraction(geo, this.elements.viewer.slider.scrollLeft);

        // Instant, always: the motion is the hero's: animating the strip on top
        // of it would trail behind by the strip's own easing — the visible lag.
        if (!this._stripInstant) {
          geo.strip.style.scrollBehavior = 'auto';
          this._stripInstant = true;
        }
        const target = this.stripLeftFor(geo, f);
        if (Math.abs(geo.strip.scrollLeft - target) > 0.5) geo.strip.scrollLeft = target;

        const near = Math.round(f);
        if (this._syncTarget) {
          // A programmatic jump is still gliding. The outline already sits on
          // the destination, and the slides passing underneath on the way must
          // not drag it there and back (the "shuffle"). Release on arrival —
          // or on the deadline, so a glide the shopper interrupts mid-flight
          // cannot strand the outline on a slide nobody is looking at.
          const arrived = geo.thumbs[near] && geo.thumbs[near].dataset.target === this._syncTarget;
          if (!arrived && Date.now() < this._syncTargetUntil) return;
          this._syncTarget = null;
        }
        this.markActiveThumbnail(geo.thumbs[near]);
      }

      // Outline only — no scrolling. Position is the hero's business.
      markActiveThumbnail(thumbnail) {
        if (!thumbnail) return;
        const button = thumbnail.querySelector('button');
        if (!button || button.hasAttribute('aria-current')) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        button.setAttribute('aria-current', true);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.markActiveThumbnail(thumbnail);

        // When the hero scrolls, followHero has already put the strip exactly
        // where this thumb belongs — scrolling again here would race it.
        if (this.heroDrivesStrip()) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        // Layouts where the hero does NOT scroll (desktop grid) get the same
        // centring rule, so both paths agree on where a given thumb sits.
        const geo = this.galleryGeometry();
        const track = this.elements.thumbnails.slider;
        const index = geo ? geo.thumbs.indexOf(thumbnail) : -1;
        if (geo && index >= 0) {
          track.scrollTo({ left: this.stripLeftFor(geo, index) });
          return;
        }
        const left = thumbnail.offsetLeft;
        const right = left + thumbnail.clientWidth;
        if (left < track.scrollLeft) track.scrollTo({ left: left });
        else track.scrollTo({ left: right - track.clientWidth });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
